'use client'

import NextLink from 'next/link'
import React, { useEffect, useState } from 'react'

import { stegaClean } from '@sanity/client/stega'

import { useDownloadModal } from '@/app/providers/DownloadModalProvider'
import { useLanguage } from '@/app/providers/LanguageProvider'
import { useSettings } from '@/app/providers/SettingsProvider'
import { buildTrackingData, trackElementClick, TrackingLocation } from '@/app/utils/analytics'
import { cn } from '@/app/utils/style'
import { Link as LinkRaw } from '@/sanity.types'
import type { DeepRequiredNullable } from '@/types/utils'

function isAbsoluteUrl(href: string) {
  try {
    const u = new URL(href)
    return !!u.protocol
  } catch {
    return false
  }
}

/**
 * Normalizes URLs by converting same-origin absolute URLs to relative paths.
 * This prevents SSR hydration mismatches when Sanity stega encoding generates
 * absolute URLs on server but relative URLs on client.
 */
function normalizeUrl(href: string): string {
  try {
    const url = new URL(href)
    // If it's an absolute URL pointing to the current origin, make it relative
    if (typeof window !== 'undefined' && url.origin === window.location.origin) {
      return url.pathname + url.search + url.hash
    }
    // On server, check against common local origins
    if (
      typeof window === 'undefined' &&
      (url.origin === 'http://localhost:3000' || url.origin === 'http://localhost:3333')
    ) {
      return url.pathname + url.search + url.hash
    }
    return href
  } catch {
    // Not a valid absolute URL, return as-is
    return href
  }
}

type LinkWithNulls = DeepRequiredNullable<LinkRaw>

type LinkWithDeref = Omit<LinkWithNulls, 'internalPage' | 'internalPost'> & {
  href?: string | null
  internalPage?: {
    _id?: string
    _type?: string
    slug?: string | null
    name?: string | null
    title?: string | null
  } | null
  internalPost?: { _id?: string; slug?: string | null; title?: string | null } | null
}

export type LinkProps = React.ComponentProps<'a'> & {
  link: LinkWithDeref
  tracking: TrackingLocation
  className?: string
  children?: React.ReactNode
}

export const Link = function ({ link, tracking, className, children, ...anchorProps }: LinkProps) {
  // Hooks must be called before any early returns
  const settings = useSettings()
  const { locale } = useLanguage()
  const { openModal } = useDownloadModal()
  const [deviceDetectedHref, setDeviceDetectedHref] = useState<string | null>(null)
  const [shouldOpenModal, setShouldOpenModal] = useState(false)

  // Clean stega encoding from entire Sanity object before using in useEffect
  const cleanLink = link ? stegaClean(link) : null

  // Handle device detection download type
  useEffect(() => {
    if (cleanLink?.linkType === 'deviceDetectionDownload') {
      const { detectDevice } = require('@/app/utils/device')
      const deviceType = detectDevice()

      if (deviceType === 'ios') {
        setDeviceDetectedHref(settings?.appStoreUrl || null)
        setShouldOpenModal(false)
      } else if (deviceType === 'android') {
        setDeviceDetectedHref(settings?.googlePlayUrl || null)
        setShouldOpenModal(false)
      } else {
        // Desktop - trigger modal instead of navigation
        setDeviceDetectedHref('#')
        setShouldOpenModal(true)
      }
    }
  }, [cleanLink?.linkType, settings?.appStoreUrl, settings?.googlePlayUrl, locale])

  if (!link || !cleanLink) return null

  const label =
    cleanLink.label ||
    cleanLink.internalPage?.name ||
    cleanLink.internalPage?.title ||
    cleanLink.internalPost?.title ||
    ''
  const ariaLabel = cleanLink.ariaLabel || label
  const openInNewTab = !!cleanLink.openInNewTab

  // Use children if provided, otherwise fall back to label
  const content = children !== undefined ? children : label
  const textContent = typeof content === 'string' ? content : label

  const finalClassName = cn('no-underline hover:no-underline', className)

  // Normalize the href to prevent SSR hydration mismatches
  let normalizedHref = cleanLink.href ? normalizeUrl(cleanLink.href) : null

  // Use device-detected href for deviceDetectionDownload type
  if (cleanLink.linkType === 'deviceDetectionDownload') {
    normalizedHref = deviceDetectedHref
  }

  // Handle click tracking and modal opening
  const handleClick = (e: React.MouseEvent) => {
    if (shouldOpenModal) {
      e.preventDefault()
      openModal()
      const trackingData = buildTrackingData('link', textContent, tracking, 'download-modal')
      trackElementClick(trackingData)
    } else if (normalizedHref) {
      const trackingData = buildTrackingData('link', textContent, tracking, normalizedHref)
      trackElementClick(trackingData)
    }
  }

  if (!normalizedHref) {
    return (
      <span className={finalClassName} aria-label={ariaLabel} {...(anchorProps as any)}>
        {content}
      </span>
    )
  }

  const isAnchorLink = normalizedHref.startsWith('#')
  const useRegularAnchor =
    !isAnchorLink &&
    (isAbsoluteUrl(normalizedHref) ||
      normalizedHref.startsWith('mailto:') ||
      normalizedHref.startsWith('tel:'))

  const rel = openInNewTab ? 'noopener noreferrer' : undefined
  const target = openInNewTab ? '_blank' : undefined

  if (useRegularAnchor) {
    return (
      <a
        href={normalizedHref}
        className={finalClassName}
        aria-label={ariaLabel}
        rel={rel}
        target={target}
        onClick={handleClick}
      >
        {content}
      </a>
    )
  }

  // Use Next.js Link for internal routes and anchor links
  return (
    <NextLink
      href={normalizedHref}
      className={finalClassName}
      aria-label={ariaLabel}
      onClick={handleClick}
    >
      {content}
    </NextLink>
  )
}
